import { ComponentProps, forwardRef, PropsWithChildren } from 'react'
import { Drawer } from 'vaul'

import {
  DraggableDrawerBackdrop,
  DraggableDrawerChildren,
  DraggableDrawerContent,
  DraggableDrawerIndicator,
} from './styled'

export type DrawerDraggableProps = PropsWithChildren & {
  /**
   * Determines if the backdrop (a slight shadow behind) is present when the drawer is open
   */
  hasBackdrop?: boolean
  /**
   * Determines if the content inside the drawer should be scrollable or not.
   */
  isContentScrollable?: boolean
  /**
   * Determines the visibility of the component
   */
  isOpen: boolean
  /**
   * Accepts a callback that's executed when the user clicks the close button
   */
  onCancel: () => void
  /**
   * Event handler called when the indicator is clicked. It is recommended to provide this for accessibility purposes when the drawer is constantly open.
   */
  onClickIndicator?: () => void
  /**
   * The aria-label for the indicator button to provide context to the screen readers.
   */
  indicatorAriaLabel?: string
} & Pick<
    ComponentProps<typeof Drawer.Root>,
    /**
     * Array of numbers from 0 to 1 that corresponds to % of the screen a given snap point should take up. You can also use px values, which doesn't take screen height into account.
     */
    | 'snapPoints'
    /**
     * The controlled snap point state
     */
    | 'activeSnapPoint'
    /**
     * Event handler called when the snap point state changes.
     */
    | 'setActiveSnapPoint'
    /**
     * When false it allows to interact with elements outside of the drawer without closing it.
     */
    | 'modal'
    /**
     * When false dragging, clicking outside, pressing esc, etc. will not close the drawer. Use this in combination with the open prop, otherwise you won't be able to open/close the drawer.
     */
    | 'dismissible'
  >

/**
 * A draggable drawer component that pops out from the bottom. Although it supports desktop view, this pattern is intended for mobile use.
 * It is a wrapper around the Drawer component from Vaul => https://vaul.emilkowal.ski/getting-started
 * It is recommended to use the DraggableDrawerTitle and DraggableDrawerDescription components to provide an accessible title and description for the drawer.
 * Additionally, to enhance accessibility, a button to open the drawer and a close button should be provided.
 */
export const DrawerDraggable = forwardRef<HTMLDivElement, DrawerDraggableProps>(
  (
    {
      activeSnapPoint,
      children,
      dismissible = true,
      hasBackdrop = false,
      indicatorAriaLabel,
      isContentScrollable = true,
      isOpen,
      modal,
      onCancel,
      onClickIndicator,
      setActiveSnapPoint,
      snapPoints,
    },
    ref
  ) => {
    const handleDrawerChange = (open: boolean) => {
      if (!open) {
        onCancel()
      }
    }

    return (
      <Drawer.Root
        open={isOpen}
        direction="bottom"
        onOpenChange={handleDrawerChange}
        snapPoints={snapPoints ?? []}
        activeSnapPoint={activeSnapPoint}
        setActiveSnapPoint={setActiveSnapPoint}
        modal={modal}
        dismissible={dismissible}
        fadeFromIndex={1}
      >
        {hasBackdrop && <DraggableDrawerBackdrop />}

        <Drawer.Portal>
          <DraggableDrawerContent>
            <DraggableDrawerIndicator
              as={onClickIndicator ? 'button' : 'span'}
              onClick={onClickIndicator || undefined}
              aria-label={indicatorAriaLabel}
            />

            <DraggableDrawerChildren
              ref={ref}
              isContentScrollable={isContentScrollable}
            >
              {children}
            </DraggableDrawerChildren>
          </DraggableDrawerContent>
        </Drawer.Portal>
      </Drawer.Root>
    )
  }
)

DrawerDraggable.displayName = 'DrawerDraggable'

// An accessible title to be announced when the drawer is opened.
export const DrawerDraggableTitle = Drawer.Title
// An optional accessible description to be announced when the drawer is opened.
export const DrawerDraggableDescription = Drawer.Description
